import { FC, useCallback, useState, useEffect } from 'react';
import { FaExclamationTriangle, FaLock } from 'react-icons/fa';

import Modal from '@components/Modal';
import Input from '@components/Input';
import Button from '@components/Button';

import { resetPasswordRequest } from '@api/requests';

import LoaderImage from '@assets/loader.gif';

import './ResetPasswordModal.scss';

// Minimum eight characters, at least one uppercase letter, one lowercase letter, one number and one special character
const passRe =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[$&+,:;=?@#|'<>.\-^*()%!]).{8,}$/;

const inputStyles = {
  marginBottom: 15,
};

const buttonStyles = {
  marginBottom: 20,
};

type ResetPasswordModalProps = {
  isOpen: boolean;
  token: string;
  email: string;
  onClose: () => void;
};

const ResetPasswordModal: FC<ResetPasswordModalProps> = ({
  isOpen,
  token,
  onClose,
  email
}) => {
  const [password, setPassword] = useState<string>('');
  const [reenteredPassword, setReenteredPassword] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [pass1Error, setPass1Error] = useState<boolean>(false);
  const [pass2Error, setPass2Error] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSucceeded, setIsSucceeded] = useState<boolean>(false);

  const handleInputChange = useCallback(e => {
    setErrors([]);
    setPass1Error(false);
    setPass2Error(false);

    const { id } = e.target.dataset;
    const value = e.target.value;

    switch (id) {
      case 'password':
        setPassword(value);
        break;
      case 'reenteredPassword':
        setReenteredPassword(value);
        break;

      default:
        break;
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    let errors = [];

    if (!passRe.test(password) || !password) {
      setPass1Error(true);
    }

    if (!passRe.test(reenteredPassword) || !reenteredPassword) {
      setPass2Error(true);
    }

    if (!passRe.test(password) || !passRe.test(reenteredPassword)) {
      errors.push(
        'password should contain minimum 8 characters, at least one uppercase letter, one lowercase letter, one number and one special character',
      );
    }

    if (!password || !reenteredPassword) {
      errors.push('all fields are required');
    }

    if (password !== reenteredPassword) {
      errors.push("passwords don't match");
    }

    setErrors(errors);

    if (!!errors.length) return;

    setIsLoading(true);

    const [err] = await resetPasswordRequest(
      password,
      reenteredPassword,
      token,
      email
    );
    setIsLoading(false);

    if (err) {
      console.error(err);
      const msg =
        'possibly, token has expired. Please try to go over the password recovery process one more time.';
      errors.push(msg);
    } else {
      setIsSucceeded(true);
    }
  }, [password, reenteredPassword, token, email]);

  useEffect(() => {
    setErrors([]);
    setPass1Error(false);
    setPass2Error(false);
    setIsSucceeded(false);
  }, [isOpen]);

  return (
    <>
      <Modal
        opened={isOpen && !isSucceeded}
        title="Reset your password"
        onClose={onClose}
        invertedColors
        crossButton
        footer={false}
        style={{ width: 600 }}
      >
        <div className="reset-password-modal">
          <label>
            <span>Enter your new password</span>
            <Input
              data-id="password"
              type="password"
              placeholder="Enter new password"
              icon={<FaLock />}
              value={password}
              onChange={handleInputChange}
              style={inputStyles}
              error={!!pass1Error}
            />
          </label>
          <label>
            <span>Re-enter password</span>
            <Input
              data-id="reenteredPassword"
              type="password"
              placeholder="Re-enter new password"
              icon={<FaLock />}
              value={reenteredPassword}
              onChange={handleInputChange}
              style={inputStyles}
              error={!!pass2Error}
            />
          </label>
          {errors.length > 0 && (
            <>
              <div className="reset-password-modal__error-message">
                <FaExclamationTriangle />
                <span>Please fix following errors before submitting:</span>
              </div>
              <ul className="reset-password-modal__error-message__list">
                {errors.map((msg, index) => (
                  <li key={index}>{msg}</li>
                ))}
              </ul>
            </>
          )}
          <Button
            label="Reset"
            disabled={isLoading}
            onClick={handleSubmit}
            style={buttonStyles}
          />
          {isLoading && (
            <div className="reset-password-modal__loading-image">
              <img src={LoaderImage} alt="" />
            </div>
          )}
        </div>
      </Modal>
      <Modal
        title="SUCCESS"
        opened={isOpen && isSucceeded}
        invertedColors
        crossButton
        onClose={onClose}
        style={{ width: 600, overflow: 'initial' }}
      >
        <div className="reset-password-modal__succeeded" style={{color:"#f0f8ff"}}>
          <h3>
            <span>Your password has been changed.</span>
          </h3>
        </div>
      </Modal>
    </>
  );
};

export default ResetPasswordModal;
